<template>
  <AModal
    :model-value="modelValue"
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="luc__title">
        <i class="material-icons-round luc__title-icon">person_search</i>
        회원 동의 이력
      </span>
    </template>
    <template #sub>{{ userEmail || `userIdx ${userIdx}` }} · 정보주체 열람 요구 대응용</template>

    <div v-if="loading" class="luc__loading">
      <div class="p-spinner p-spinner--lg"></div>
      <p>이력을 불러오는 중...</p>
    </div>

    <div v-else class="luc">
      <section class="luc__sec">
        <h4 class="luc__sec-title">문서별 최신 상태</h4>
        <ATable v-if="latest.length" :columns="latestColumns" :rows="latest" dense striped bordered>
          <template #cell:version="{ row }">
            <span class="luc__mono">v{{ row.version }}</span>
          </template>
          <template #cell:agreed="{ row }">
            <APill :kind="row.agreed ? 'ok' : 'err'" dot>{{ row.agreed ? '동의' : '철회' }}</APill>
          </template>
          <template #cell:agreedAt="{ row }">
            <span class="luc__date">{{ fmtKst(row.agreedAt) }}</span>
          </template>
        </ATable>
        <div v-else class="luc__empty">최신 동의 상태가 없습니다.</div>
      </section>

      <section class="luc__sec">
        <h4 class="luc__sec-title">전체 이력 ({{ history.length }}건)</h4>
        <ATable v-if="history.length" :columns="historyColumns" :rows="history" dense striped bordered>
          <template #cell:version="{ row }">
            <span class="luc__mono">v{{ row.version }}</span>
          </template>
          <template #cell:agreed="{ row }">
            <APill :kind="row.agreed ? 'ok' : 'err'" dot>{{ row.agreed ? '동의' : '철회' }}</APill>
          </template>
          <template #cell:source="{ row }">{{ consentSourceLabel(row.source) }}</template>
          <template #cell:agreedAt="{ row }">
            <span class="luc__date">{{ fmtKst(row.agreedAt) }}</span>
          </template>
        </ATable>
        <div v-else class="luc__empty">이력이 없습니다.</div>
      </section>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtKst, consentSourceLabel } from "@/utils/legal";

export default {
  name: "LegalUserConsentModal",
  components: { AModal, ABtn, APill, ATable },
  props: {
    modelValue: { type: Boolean, default: false },
    userIdx: { type: [Number, String], default: "" },
    userEmail: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      latest: [],
      history: [],
      latestColumns: [
        { key: "documentId", label: "문서", width: 180, mono: true },
        { key: "version", label: "버전", width: 70, align: "center" },
        { key: "agreed", label: "상태", width: 80, align: "center" },
        { key: "agreedAt", label: "시각 (KST)", width: 140 },
      ],
      historyColumns: [
        { key: "documentId", label: "문서", width: 170, mono: true },
        { key: "version", label: "버전", width: 70, align: "center" },
        { key: "agreed", label: "상태", width: 80, align: "center" },
        { key: "source", label: "경로", width: 110, align: "center" },
        { key: "agreedAt", label: "시각 (KST)", width: 140 },
      ],
    };
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },
  methods: {
    fmtKst,
    consentSourceLabel,
    async load() {
      if (!this.userIdx) return;
      this.loading = true;
      this.latest = [];
      this.history = [];
      try {
        const res = await this.$axios({
          method: "get",
          url: `/emp/legal/consents/users/${encodeURIComponent(this.userIdx)}`,
          loadingBar: true,
        });
        this.latest = res.data?.latest || [];
        this.history = res.data?.history || [];
      } catch (e) {
        console.error("회원 동의 이력 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "동의 이력을 불러오지 못했습니다."), "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.luc {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 56px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__sec-title {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
  }

  &__empty {
    padding: 24px;
    text-align: center;
    font-size: 12px;
    color: $p-ink-soft;
    border: 1px dashed $p-line;
    border-radius: $p-radius-md;
  }

  &__mono {
    font-family: $p-font-mono;
    font-weight: 700;
  }

  &__date {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-mid;
  }
}
</style>
